import { useGrants, usePeople } from '@/db/hooks'
import { followMembers, followPatch, isFollowing } from '@/lib/follow'
import { useAccess } from '@/state/access'
import { mutate } from '@/runtime'
import { SwitchRow } from './Switch'

/**
 * "Keep Keira posted": one switch per workspace member, on a task or a project
 * (a project's covers its tasks), the app's SwitchRow. It writes at once; a member cannot set it.
 */
export const KeepPosted = ({
  id,
  labels,
  parentLabels,
  parentTitle,
}: {
  id: string
  labels: Record<string, string>
  /** the project this task sits in: its label covers the task */
  parentLabels?: Record<string, string>
  parentTitle?: string
}) => {
  const isMember = useAccess((s) => s.member)
  const members = followMembers(useGrants(), new Date(), usePeople())
  if (isMember || members.length === 0) return null
  return (
    <div className="mb-3" data-testid="keep-posted">
      {members.map((m) => {
        const own = m.ids.some((id) => isFollowing(labels, id))
        const through = !own && !!parentLabels && m.ids.some((id) => isFollowing(parentLabels, id))
        return (
          <SwitchRow
            key={m.id}
            label={`Keep ${m.name} posted`}
            hint={
              through
                ? `Through ${parentTitle || 'its project'}. Switching off here only clears this task’s own setting; ${parentTitle || 'the project'} still covers it.`
                : 'She is told when it is reminded, done, moved or late.'
            }
            on={own || through}
            onChange={(on) =>
              void mutate.patch(id, {
                labels: Object.assign({}, ...m.ids.map((gid) => followPatch(gid, on))) as Record<
                  string,
                  string | null
                >,
              })
            }
            testId={`keep-posted-${m.name.toLowerCase()}`}
          />
        )
      })}
    </div>
  )
}
